"use client";

import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import React from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { CampaignFormMessages } from "@/messages/campaigns/campaign-form.messages";

type WeekDay = { key: string; label: string; index: number };

type Props = {
  messages: CampaignFormMessages;
  HOUR_OPTIONS: readonly string[];
  selectedHours: string[];
  setSelectedHours: (hours: string[]) => void;
  addHourToSelection: (h: string) => void;
  toggleHour: (h: string) => void;
  customHour: string;
  setCustomHour: (v: string) => void;
  WEEK_DAYS: readonly WeekDay[];
  selectedWeekDays: string[];
  toggleWeekDay: (key: string) => void;
  specificDayTemp: any;
  setSpecificDayTemp: (d: any) => void;
  startDate?: any;
  endDate?: any;
  specificDays: any[];
  setSpecificDays: (days: any[]) => void;
  addSpecificDay: () => void;
  programmedDateTemp: any;
  setProgrammedDateTemp: (d: any) => void;
  fixedProgramTime: string;
  setFixedProgramTime: (t: string) => void;
  programmedDates: any[];
  setProgrammedDates: (dates: any[] | ((prev: any[]) => any[])) => void;
  addProgrammedDate: () => void;
  generateSchedules: () => void;
  handleClearSchedules?: () => void;
  schedulingError?: string;
  isEditMode?: boolean;
};

const SchedulingBuilder = ({
  messages,
  HOUR_OPTIONS,
  selectedHours,
  setSelectedHours,
  addHourToSelection,
  toggleHour,
  customHour,
  setCustomHour,
  WEEK_DAYS,
  selectedWeekDays,
  toggleWeekDay,
  specificDayTemp,
  setSpecificDayTemp,
  startDate,
  endDate,
  specificDays,
  setSpecificDays,
  addSpecificDay,
  programmedDateTemp,
  setProgrammedDateTemp,
  fixedProgramTime,
  setFixedProgramTime,
  programmedDates,
  setProgrammedDates,
  addProgrammedDate,
  generateSchedules,
  handleClearSchedules,
  schedulingError,
  isEditMode,
}: Props) => (
  <>
    <Grid size={{ xs: 12 }}>
      <Paper
        elevation={0}
        sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}
      >
        <MDTypography variant="h6">{messages.schedulingHoursTitle}</MDTypography>
        <MDTypography variant="caption" color="text">
          {messages.schedulingHoursDescription}
        </MDTypography>
        <MDBox mt={3}>
          <ToggleButtonGroup
            size="small"
            value={selectedHours}
            onChange={(_, val) => val && setSelectedHours(val)}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              "& .MuiToggleButton-root": {
                minWidth: "70px",
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              },
            }}
          >
            {HOUR_OPTIONS.map((h) => (
              <ToggleButton key={h} value={h} selected={selectedHours.includes(h)}>
                {h}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </MDBox>

        {selectedHours.length > 0 && (
          <MDBox mt={2} display="flex" gap={1} flexWrap="wrap">
            {selectedHours.map((h) => (
              <Chip
                key={h}
                label={h}
                onDelete={() => toggleHour(h)}
                size="medium"
                color="default"
                variant="outlined"
              />
            ))}
          </MDBox>
        )}

        <MDBox mt={3}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                label={messages.schedulingCustomHourLabel}
                type="time"
                fullWidth
                value={customHour}
                onChange={(e) => setCustomHour(e.target.value)}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => {
                  if (customHour) {
                    addHourToSelection(customHour);
                    setCustomHour("");
                  }
                }}
                disabled={!customHour}
                sx={{
                  color: "text.primary",
                  borderColor: "divider",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                {messages.schedulingAddButtonLabel}
              </Button>
            </Grid>
          </Grid>
        </MDBox>
      </Paper>
    </Grid>

    <Grid size={{ xs: 12 }}>
      <Paper
        elevation={0}
        sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}
      >
        <MDTypography variant="h6">{messages.schedulingWeekDaysTitle}</MDTypography>
        <MDTypography variant="caption" color="text">
          {messages.schedulingWeekDaysDescription}
        </MDTypography>
        <MDBox mt={3} display="flex" flexWrap="wrap" gap={1}>
          {WEEK_DAYS.map((d) => (
            <Chip
              key={d.key}
              label={d.label}
              color="default"
              variant={selectedWeekDays.includes(d.key) ? "filled" : "outlined"}
              onClick={() => toggleWeekDay(d.key)}
              size="medium"
              sx={{ cursor: "pointer" }}
            />
          ))}
        </MDBox>
      </Paper>
    </Grid>

    <Grid size={{ xs: 12 }}>
      <Paper
        elevation={0}
        sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}
      >
        <MDTypography variant="h6">{messages.schedulingSpecificDaysTitle}</MDTypography>
        <MDTypography variant="caption" color="text">
          {messages.schedulingSpecificDaysDescription}
        </MDTypography>
        <Grid container spacing={2} sx={{ mt: 2 }} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 8 }}>
            <DatePicker
              label={messages.schedulingAddSpecificDateLabel}
              value={specificDayTemp}
              onChange={(newDate) => setSpecificDayTemp(newDate)}
              minDate={startDate}
              maxDate={endDate}
              disablePast
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                  InputLabelProps: { shrink: true },
                  error: false,
                },
                layout: {
                  sx: {
                    "& .MuiDivider-root": { margin: 0 },
                    backgroundImage: "unset",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              onClick={addSpecificDay}
              disabled={!specificDayTemp}
              sx={{
                color: "text.primary",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              {messages.schedulingAddButtonLabel}
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <MDBox display="flex" flexWrap="wrap" gap={1}>
              {specificDays
                .filter((d) => d && d.isValid())
                .map((d, idx) => (
                  <Chip
                    key={`specific-${idx}-${d.format("YYYY-MM-DD")}`}
                    label={d.format("DD/MM/YYYY")}
                    onDelete={() =>
                      setSpecificDays(
                        specificDays.filter((x) => x.isValid() && !x.isSame(d, "day"))
                      )
                    }
                    size="medium"
                    color="default"
                    variant="outlined"
                  />
                ))}
            </MDBox>
          </Grid>
        </Grid>
      </Paper>
    </Grid>

    <Grid size={{ xs: 12 }}>
      <Paper
        elevation={0}
        sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}
      >
        <MDTypography variant="h6">{messages.schedulingProgrammedDatesTitle}</MDTypography>
        <MDTypography variant="caption" color="text">
          {messages.schedulingProgrammedDatesDescription}
        </MDTypography>
        <Grid container spacing={2} sx={{ mt: 2 }} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 8 }}>
            <DateTimePicker
              label={messages.schedulingAddProgrammedDateLabel}
              value={programmedDateTemp}
              onChange={(d) => setProgrammedDateTemp(d)}
              disablePast
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                  InputLabelProps: { shrink: true },
                },
                layout: {
                  sx: {
                    "& .MuiDivider-root": { margin: 0 },
                    backgroundImage: "unset",
                  },
                },
                digitalClockSectionItem: {
                  style: {
                    minWidth: "unset",
                  },
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon />}
              onClick={addProgrammedDate}
              disabled={!programmedDateTemp}
              sx={{
                color: "text.primary",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              {messages.schedulingAddButtonLabel}
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <MDBox display="flex" flexWrap="wrap" gap={1}>
              {programmedDates
                .filter((d) => d && d.isValid())
                .map((d, idx) => (
                  <Chip
                    key={`programmed-${idx}-${d.format("YYYY-MM-DD-HH-mm")}`}
                    label={d.format("DD/MM/YYYY HH:mm")}
                    onDelete={() =>
                      setProgrammedDates(
                        programmedDates.filter((x) => x.isValid() && !x.isSame(d, "minute"))
                      )
                    }
                    size="medium"
                    color="default"
                    variant="outlined"
                  />
                ))}
            </MDBox>
          </Grid>
        </Grid>
      </Paper>
    </Grid>

    <Grid size={{ xs: 12 }}>
      <Divider sx={{ my: 2 }} />
      <MDBox display="flex" justifyContent="flex-end" gap={2}>
        {!isEditMode && handleClearSchedules && (
          <MDButton variant="gradient" color="light" onClick={handleClearSchedules}>
            {messages.schedulingClearSchedulesButtonLabel}
          </MDButton>
        )}
        <MDButton variant="gradient" color="dark" onClick={generateSchedules}>
          {messages.schedulingApplyButtonLabel}
        </MDButton>
      </MDBox>
      {schedulingError && (
        <Alert severity="error" sx={{ mt: 2, alignItems: "center" }}>
          <MDTypography variant="body2">{schedulingError}</MDTypography>
        </Alert>
      )}
    </Grid>
  </>
);

export default SchedulingBuilder;
